import { supabase } from '../lib/supabase';
import {
  ProjectMember,
  ProjectMemberWithUser,
  AddProjectMemberInput,
  UpdateProjectMemberInput,
  UserSearchResult,
  MemberStatus
} from '../types/projectMember';

class ProjectMemberService {
  // Buscar todos os membros de um projeto com dados completos
  async getProjectMembers(projectId: string): Promise<ProjectMemberWithUser[]> {
    try {
      // Buscar membros do projeto
      const { data: members, error: membersError } = await supabase
        .from('project_team_members')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (membersError) throw membersError;

      if (!members || members.length === 0) return [];

      // Buscar dados dos usuários no user_profile
      const userIds = members.map(m => m.user_id).filter(Boolean);

      if (userIds.length === 0) {
        // Só membros externos, retornar sem dados de usuário
        return members.map(m => ({
          ...m,
          user: null
        }));
      }

      const { data: userProfiles, error: profilesError } = await supabase
        .from('user_profile')
        .select('auth_id, name, email, user_photo, bio')
        .in('auth_id', userIds);

      if (profilesError) {
        console.warn('Erro ao buscar perfis de usuários:', profilesError);
        return members.map(m => ({
          ...m,
          user: null
        }));
      }

      // Mapear perfis por auth_id
      const profilesMap = new Map(
        (userProfiles || []).map(p => [p.auth_id, p])
      );

      // Combinar dados
      return members.map(member => {
        const profile = member.user_id ? profilesMap.get(member.user_id) : null;
        return {
          ...member,
          user: profile ? {
            id: profile.auth_id,
            name: profile.name,
            email: profile.email,
            avatar_url: profile.user_photo,
            department: profile.bio
          } : null
        };
      });
    } catch (error) {
      console.error('Erro ao buscar membros do projeto:', error);
      throw error;
    }
  }

  // Buscar usuários da organização para adicionar ao projeto
  async searchOrganizationUsers(
    organizationId: string,
    projectId: string,
    searchTerm?: string
  ): Promise<UserSearchResult[]> {
    try {
      // Primeiro, buscar todos os usuários da organização através do user_profile
      const { data: orgMembers, error: orgError } = await supabase
        .from('user_profile')
        .select(`
          id,
          auth_id,
          name,
          email,
          user_photo,
          bio
        `)
        .eq('organization_id', organizationId);

      if (orgError) throw orgError;

      // Buscar membros atuais do projeto para marcar quem já é membro
      const { data: projectMembers, error: projectError } = await supabase
        .from('project_team_members')
        .select('user_id')
        .eq('project_id', projectId)
        .not('user_id', 'is', null);

      if (projectError) throw projectError;

      const projectMemberIds = new Set(projectMembers?.map(m => m.user_id) || []);

      // Formatar resultados
      const users: UserSearchResult[] = (orgMembers || [])
        .filter(member => {
          if (!searchTerm) return true;
          const term = searchTerm.toLowerCase();
          return (
            member.name?.toLowerCase().includes(term) ||
            member.email?.toLowerCase().includes(term)
          );
        })
        .map(member => ({
          id: member.id,
          auth_id: member.auth_id,
          name: member.name || 'Usuário sem nome',
          email: member.email || '',
          avatar_url: member.user_photo,
          department: member.bio,
          is_member: projectMemberIds.has(member.auth_id)
        }));

      return users;
    } catch (error) {
      console.error('Erro ao buscar usuários da organização:', error);
      throw error;
    }
  }

  // Adicionar membro ao projeto (usuário existente ou externo)
  async addProjectMember(input: AddProjectMemberInput): Promise<ProjectMember> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const memberData: any = {
        project_id: input.project_id,
        role: input.role,
        status: 'active',
        created_at: new Date().toISOString()
      };

      if (input.user_id) {
        // Membro interno (usuário do sistema)
        memberData.user_id = input.user_id;
        memberData.invitation_status = 'active';
        
        // Buscar dados do usuário para preencher nome e email
        const { data: userData } = await supabase
          .from('user_profile')
          .select('name, email')
          .eq('auth_id', input.user_id)
          .single();

        if (userData) {
          memberData.name = userData.name;
          memberData.email = userData.email;
        }
      } else {
        // Membro externo
        memberData.name = input.name;
        memberData.email = input.email;
        memberData.invitation_status = 'external';
      }

      // Adicionar campos opcionais
      if (input.department) memberData.department = input.department;
      if (input.permissions) memberData.permissions = input.permissions;
      if (input.start_date) memberData.start_date = input.start_date;
      if (input.end_date) memberData.end_date = input.end_date;

      const { data, error } = await supabase
        .from('project_team_members')
        .insert(memberData)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erro ao adicionar membro ao projeto:', error);
      throw error;
    }
  }

  // Convidar usuário por email
  async inviteProjectMember(
    projectId: string,
    email: string,
    role: string
  ): Promise<ProjectMember> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Verificar se o email já existe no sistema
      const { data: existingUser } = await supabase
        .from('user_profile')
        .select('auth_id, name')
        .eq('email', email)
        .single();

      const memberData: any = {
        project_id: projectId,
        email: email,
        role: role,
        invitation_status: existingUser ? 'pending' : 'invited',
        invited_by: user.id,
        invited_at: new Date().toISOString(),
        status: 'inactive' // Fica inativo até aceitar
      };

      if (existingUser) {
        memberData.user_id = existingUser.auth_id;
        memberData.name = existingUser.name;
      } else {
        memberData.name = email.split('@')[0]; // Nome temporário
      }

      const { data, error } = await supabase
        .from('project_team_members')
        .insert(memberData)
        .select()
        .single();

      if (error) throw error;

      // TODO: Enviar email de convite

      return data;
    } catch (error) {
      console.error('Erro ao convidar membro:', error);
      throw error;
    }
  }

  // Atualizar membro do projeto
  async updateProjectMember(
    memberId: string,
    updates: UpdateProjectMemberInput
  ): Promise<ProjectMember> {
    try {
      const { data, error } = await supabase
        .from('project_team_members')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', memberId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erro ao atualizar membro:', error);
      throw error;
    }
  }

  // Remover membro do projeto
  async removeProjectMember(memberId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('project_team_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao remover membro:', error);
      throw error;
    }
  }

  // Aceitar convite para projeto
  async acceptProjectInvite(memberId: string): Promise<ProjectMember> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase
        .from('project_team_members')
        .update({
          invitation_status: 'active',
          status: 'active',
          accepted_at: new Date().toISOString(),
          user_id: user.id
        })
        .eq('id', memberId)
        .eq('email', user.email) // Verificar que o convite é para este usuário
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Erro ao aceitar convite:', error);
      throw error;
    }
  }

  // Sincronizar TODOS os membros do projeto com chat (nova versão robusta)
  async syncAllProjectMembersWithChat(projectId: string): Promise<number> {
    try {
      console.log(`Sincronizando projeto ${projectId} com chat...`);
      
      // Usar a nova função SQL robusta
      const { data, error } = await supabase
        .rpc('sync_project_to_chat', {
          target_project_id: projectId
        });

      if (error) throw error;

      const results = data || [];
      const successful = results.filter((r: any) => r.success);
      const failed = results.filter((r: any) => !r.success);

      console.log(`Sincronização completa para projeto ${projectId}:`);
      console.log(`- ${successful.length} membros sincronizados com sucesso`);
      console.log(`- ${failed.length} erros`);

      if (failed.length > 0) {
        console.warn('Erros na sincronização:', failed);
      }

      return successful.length;
    } catch (error) {
      console.error('Erro ao sincronizar projeto com chat:', error);
      throw error;
    }
  }

  // Sincronizar TODOS os projetos do sistema
  async syncAllProjectsWithChat(): Promise<{ synced: number; errors: number; projects: any[] }> {
    try {
      console.log('Sincronizando todos os projetos com chat...');
      
      const { data, error } = await supabase
        .rpc('sync_all_projects_to_chat');

      if (error) throw error;

      const results = data || [];
      const totalSynced = results.reduce((sum: number, r: any) => sum + r.members_synced, 0);
      const totalErrors = results.reduce((sum: number, r: any) => sum + r.errors, 0);

      console.log(`Sincronização global completa:`);
      console.log(`- ${results.length} projetos processados`);
      console.log(`- ${totalSynced} membros sincronizados`);
      console.log(`- ${totalErrors} erros`);

      return {
        synced: totalSynced,
        errors: totalErrors,
        projects: results
      };
    } catch (error) {
      console.error('Erro ao sincronizar todos os projetos:', error);
      throw error;
    }
  }

  // Sincronizar membro individual com chat (mantido para compatibilidade)
  async syncMemberWithChat(memberId: string): Promise<void> {
    try {
      // Buscar dados do membro
      const { data: member, error: memberError } = await supabase
        .from('project_team_members')
        .select('*')
        .eq('id', memberId)
        .single();

      if (memberError) throw memberError;

      // TODOS os membros com user_id devem estar no chat, independente do status
      if (!member.user_id) {
        console.log('Membro externo, não sincroniza com chat:', member.name);
        return;
      }

      // Buscar sala de chat do projeto
      const { data: chatRoom, error: roomError } = await supabase
        .from('chat_rooms')
        .select('id')
        .eq('project_id', member.project_id)
        .single();

      if (roomError) {
        console.error('Sala de chat não encontrada para o projeto');
        return;
      }

      // Adicionar ao chat (SEMPRE, independente do status).
      // Sem organization_id: chat_room_members não tem a coluna em produção.
      const { error: chatError } = await supabase
        .from('chat_room_members')
        .upsert({
          room_id: chatRoom.id,
          user_auth_id: member.user_id,
          role: ['Project Manager', 'Team Lead'].includes(member.role) ? 'admin' : 'member'
        }, {
          onConflict: 'room_id,user_auth_id'
        });

      if (chatError) throw chatError;

      console.log(`Membro ${member.name} sincronizado automaticamente com chat`);
    } catch (error) {
      console.error('Erro ao sincronizar membro com chat:', error);
      throw error;
    }
  }

  // Remover membro específico do chat (para admins)
  async removeMemberFromChat(projectId: string, userAuthId: string): Promise<void> {
    try {
      // Buscar sala de chat do projeto
      const { data: chatRoom, error: roomError } = await supabase
        .from('chat_rooms')
        .select('id')
        .eq('project_id', projectId)
        .single();

      if (roomError) throw roomError;

      // Remover do chat (mas mantém no projeto)
      const { error: removeError } = await supabase
        .from('chat_room_members')
        .delete()
        .eq('room_id', chatRoom.id)
        .eq('user_auth_id', userAuthId);

      if (removeError) throw removeError;

      console.log(`Membro ${userAuthId} removido do chat (mas mantido no projeto)`);
    } catch (error) {
      console.error('Erro ao remover membro do chat:', error);
      throw error;
    }
  }

  // Buscar membros para menção no chat (TODOS os membros do projeto)
  async getMembersForMention(projectId: string): Promise<Array<{
    id: string;
    name: string;
    avatar?: string;
  }>> {
    try {
      // Buscar membros do projeto
      const { data: members, error: membersError } = await supabase
        .from('project_team_members')
        .select('user_id, name, email')
        .eq('project_id', projectId)
        .not('user_id', 'is', null); // Só usuários reais (não externos)

      if (membersError) throw membersError;

      // Buscar dados dos usuários no user_profile para pegar avatar
      const userIds = (members || []).map(m => m.user_id).filter(Boolean);

      if (userIds.length === 0) return [];

      const { data: userProfiles, error: profilesError } = await supabase
        .from('user_profile')
        .select('auth_id, name, user_photo')
        .in('auth_id', userIds);

      if (profilesError) {
        console.warn('Erro ao buscar perfis de usuários:', profilesError);
        // Retornar membros sem avatar
        return (members || []).map(member => ({
          id: member.user_id,
          name: member.name || member.email || 'Usuário',
          avatar: undefined
        }));
      }

      // Mapear perfis por auth_id
      const profilesMap = new Map(
        (userProfiles || []).map(p => [p.auth_id, p])
      );

      return (members || [])
        .map(member => {
          const profile = profilesMap.get(member.user_id);
          return {
            id: member.user_id,
            name: profile?.name || member.name || member.email || 'Usuário',
            avatar: profile?.user_photo
          };
        });
    } catch (error) {
      console.error('Erro ao buscar membros para menção:', error);
      return [];
    }
  }
}

export const projectMemberService = new ProjectMemberService();